import { htmlToBlocks, type Block } from '@textbox/core';
import { el } from './ui.ts';

const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Blocks → HTML for the contenteditable (the inverse of core's htmlToBlocks). */
export function blocksToHtml(blocks: Block[]): string {
  const span = (s: { text: string; bold?: boolean; href?: string }) => {
    let h = escape(s.text);
    if (s.bold) h = `<b>${h}</b>`;
    if (s.href) h = `<a href="${escape(s.href)}">${h}</a>`;
    return h;
  };
  return blocks.map((b) => (b.type === 'paragraph' ? `<p>${b.spans.map(span).join('')}</p>` : `<ul>${b.items.map((i) => `<li>${i.map(span).join('')}</li>`).join('')}</ul>`)).join('');
}

/**
 * The body editor: a contenteditable with the brief's minimal toolbar
 * (bold, link, list). Everything else is stripped on the way out.
 */
export class RichText {
  root: HTMLElement;
  private area: HTMLElement;
  private onChange: () => void;

  constructor(initial: Block[], onChange: () => void) {
    this.onChange = onChange;
    this.area = el('div', { class: 'richtext', contenteditable: 'true', spellcheck: 'true' });
    this.area.innerHTML = blocksToHtml(initial) || '<p></p>';
    const cmd = (name: string, arg?: string) => () => { this.area.focus(); document.execCommand(name, false, arg); onChange(); };
    const toolbar = el('div', { class: 'toolbar' },
      el('button', { type: 'button', title: 'Bold', onClick: cmd('bold') }, el('b', {}, 'B')),
      el('button', { type: 'button', title: 'Link', onClick: () => { const url = prompt('Link URL (https://…)'); if (url) cmd('createLink', url)(); } }, 'Link'),
      el('button', { type: 'button', title: 'Bulleted list', onClick: cmd('insertUnorderedList') }, '• List'),
      el('button', { type: 'button', title: 'Paragraph', onClick: cmd('formatBlock', 'p') }, '¶'),
      el('button', { type: 'button', title: 'Remove formatting', onClick: cmd('removeFormat') }, 'Plain'),
    );
    this.area.addEventListener('input', onChange);
    this.area.addEventListener('paste', (e) => {
      // paste as plain text so no foreign markup sneaks in
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain') ?? '';
      document.execCommand('insertText', false, text);
      onChange();
    });
    this.root = el('div', { class: 'richtext-wrap' }, toolbar, this.area);
  }

  blocks(): Block[] {
    return htmlToBlocks(this.area.innerHTML);
  }

  focus(): void {
    this.area.focus();
    void this.onChange;
  }
}
